import { AppIcon } from '@/components/ui/app-icon';
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  iconArrowsRotate,
  iconChevronRight,
  iconCircle,
  iconCircleCheck,
  iconTriangleExclamation,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import { ConnectCard, ConnectFrame } from './connect-mailbox-frame.component';
import {
  CONNECT_PROVIDERS,
  connectProvider,
  type ConnectPhase,
  type ConnectProvider,
} from './connect-mailbox.util';
import { ProviderMark } from './provider-mark.component';

/** Which provider: the whole row is the button. */
export function ChooseView({
  onPick,
  onCancel,
}: {
  onPick: (provider: ConnectProvider) => void;
  onCancel: () => void;
}) {
  return (
    <ConnectFrame
      title='Connect a mailbox'
      label='Choose the provider your mailbox is with'
      footer={
        <Button variant='outline' size='sm' onClick={onCancel}>
          Cancel
        </Button>
      }
    >
      <div className='space-y-2'>
        {CONNECT_PROVIDERS.map((option) => (
          <button
            key={option.key}
            type='button'
            onClick={() => onPick(option.key)}
            className='group flex w-full items-center gap-3 rounded-xl border border-border px-3 py-2.5 text-left transition-colors outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring'
          >
            <ProviderMark provider={option.mark} boxed />
            <span className='min-w-0 flex-1 text-sm font-medium'>
              {option.label}
            </span>
            <AppIcon
              icon={iconChevronRight}
              className='shrink-0 text-tertiary-foreground transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none'
              style={{ fontSize: 11 }}
            />
          </button>
        ))}
      </div>
    </ConnectFrame>
  );
}

type StepState = 'done' | 'now' | 'todo';

function StepRow({ label, state }: { label: string; state: StepState }) {
  return (
    <li
      aria-current={state === 'now' ? 'step' : undefined}
      className='flex items-center gap-2.5 px-3.5 py-2.5 text-sm'
    >
      {state === 'done' ? (
        <AppIcon
          icon={iconCircleCheck}
          className='text-emerald-600 dark:text-emerald-400'
          style={{ fontSize: 15 }}
        />
      ) : (
        <AppIcon
          icon={iconCircle}
          mono
          className={
            state === 'now' ? 'text-primary' : 'text-muted-foreground/50'
          }
          style={{ fontSize: 15 }}
        />
      )}
      <span
        className={cn(
          'min-w-0 flex-1',
          state === 'now' ? 'font-medium' : 'text-muted-foreground'
        )}
      >
        {label}
      </span>
      {state === 'now' && (
        <Badge variant='info' className='px-2 py-0 text-[10px]'>
          Now
        </Badge>
      )}
    </li>
  );
}

/** The lead of a state: a boxed mark or icon, what is happening, its state. */
function StateLead({
  mark,
  label,
  state,
  trailing,
}: {
  mark: ReactNode;
  label: string;
  state: string;
  trailing?: ReactNode;
}) {
  return (
    <div className='flex items-center gap-3'>
      {mark}
      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm font-semibold'>{label}</p>
        <p className='truncate text-xs text-muted-foreground'>{state}</p>
      </div>
      {trailing}
    </div>
  );
}

/** The sign-in window is open, or the provider has said it finished. */
export function WaitingView({
  phase,
  onReopen,
  onCancel,
}: {
  phase: Extract<ConnectPhase, { kind: 'starting' | 'waiting' }>;
  onReopen: () => void;
  onCancel: () => void;
}) {
  const provider = connectProvider(phase.provider);
  const opening = phase.kind === 'starting';
  const returned = phase.kind === 'waiting' && phase.returned;
  return (
    <ConnectFrame
      title={`Connect ${provider.name}`}
      label={`Sign in to ${provider.name} in the window that opened`}
      footer={
        <>
          {!returned && (
            <Button
              variant='ghost'
              size='sm'
              disabled={opening}
              onClick={onReopen}
              className='mr-auto text-muted-foreground'
            >
              Reopen sign-in
            </Button>
          )}
          <Button variant='outline' size='sm' onClick={onCancel}>
            Cancel
          </Button>
        </>
      }
    >
      <div role='status' aria-live='polite' className='space-y-4'>
        <StateLead
          mark={
            <ProviderMark
              provider={provider.mark}
              boxed
              className='h-11 w-11'
            />
          }
          label={
            opening
              ? `Opening ${provider.name}`
              : `Waiting for ${provider.name}`
          }
          state={
            opening
              ? 'Sign-in window opening'
              : returned
                ? 'Signed in, adding the address'
                : 'Sign-in window open'
          }
          trailing={
            <AppIcon
              icon={iconArrowsRotate}
              className='shrink-0 text-muted-foreground motion-safe:animate-spin'
              style={{ fontSize: 15 }}
              aria-hidden
            />
          }
        />
        <ConnectCard>
          <ol className='divide-y divide-border/60'>
            <StepRow label='Sign in' state={returned ? 'done' : 'now'} />
            <StepRow
              label='Check the address'
              state={returned ? 'now' : 'todo'}
            />
            <StepRow label='Set the daily limit' state='todo' />
          </ol>
        </ConnectCard>
      </div>
    </ConnectFrame>
  );
}

function FailureLead({
  label,
  state,
  mark,
}: {
  label: string;
  state: ReactNode;
  mark?: ReactNode;
}) {
  return (
    <div className='flex items-start gap-3'>
      {mark ?? (
        <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400'>
          <AppIcon icon={iconTriangleExclamation} style={{ fontSize: 15 }} />
        </span>
      )}
      <div className='min-w-0 flex-1 pt-0.5'>
        <p className='text-sm font-semibold'>{label}</p>
        <div className='mt-0.5 text-xs text-muted-foreground'>{state}</div>
      </div>
    </div>
  );
}

/** The window was closed, the address is slow, or the provider ended it. */
export function TroubleView({
  phase,
  onCancel,
  onTryAgain,
  onCheckAgain,
}: {
  phase: Extract<ConnectPhase, { kind: 'closed' | 'slow' | 'failed' }>;
  onCancel: () => void;
  onTryAgain: (provider: ConnectProvider) => void;
  onCheckAgain: () => void;
}) {
  const { name } = connectProvider(phase.provider);
  const slow = phase.kind === 'slow';
  return (
    <ConnectFrame
      label={
        slow
          ? `${name} is still adding the address`
          : `${name} was not connected`
      }
      footer={
        <>
          <Button variant='outline' size='sm' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size='sm'
            onClick={() => (slow ? onCheckAgain() : onTryAgain(phase.provider))}
          >
            {slow ? 'Check again' : 'Try again'}
          </Button>
        </>
      }
    >
      <div role='alert'>
        {phase.kind === 'closed' && (
          <FailureLead
            label={`${name} sign-in was closed`}
            state='Nothing was connected'
          />
        )}
        {phase.kind === 'slow' && (
          <FailureLead
            label={`Still waiting for ${name}`}
            state='Your address is not showing yet'
          />
        )}
        {phase.kind === 'failed' && (
          <FailureLead
            label={`Could not connect ${name}`}
            state={phase.message}
          />
        )}
      </div>
    </ConnectFrame>
  );
}

/** The address was stored and healthy before this sign-in. */
export function AlreadyView({
  email,
  provider,
  ownerName,
  shared,
  onClose,
  onUseAnother,
}: {
  email: string;
  provider: string;
  /** "You" or the teammate's name. */
  ownerName: string;
  shared: boolean;
  onClose: () => void;
  onUseAnother: () => void;
}) {
  return (
    <ConnectFrame
      label='This address is already connected'
      footer={
        <>
          <Button variant='outline' size='sm' onClick={onClose}>
            Close
          </Button>
          <Button size='sm' onClick={onUseAnother}>
            Use another account
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <FailureLead
          label='Already connected'
          state={email}
          mark={<ProviderMark provider={provider} boxed className='h-9 w-9' />}
        />
        <p className='text-sm text-muted-foreground'>
          {'By ' + ownerName}
          {shared ? ' · Shared with the team' : ''}
        </p>
      </div>
    </ConnectFrame>
  );
}

/** The API stored nothing, and says why (the plan's sender limit). */
export function RefusedView({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}) {
  return (
    <ConnectFrame
      label='The mailbox could not be added'
      footer={
        <Button variant='outline' size='sm' onClick={onClose}>
          Close
        </Button>
      }
    >
      <div role='alert'>
        <FailureLead label='Could not add this mailbox' state={message} />
      </div>
    </ConnectFrame>
  );
}
